import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4" }
const _hoisted_2 = { class: "flex items-center gap-4" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "text-lg leading-6 font-semibold text-primary-color-700" }
const _hoisted_6 = {
  key: 0,
  class: "flex flex-col pt-3"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "text-base leading-6 font-semibold text-gray-700 ml-4" }
const _hoisted_12 = {
  key: 0,
  class: "flex flex-col pt-3 ml-4"
}
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "" }
const _hoisted_15 = { key: 1 }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "text-base leading-6 font-semibold text-gray-700 capitalize" }
const _hoisted_19 = {
  key: 0,
  class: "flex flex-col pt-3"
}
const _hoisted_20 = ["onClick"]
const _hoisted_21 = {
  key: 1,
  class: "flex flex-col gap-1"
}
const _hoisted_22 = {
  key: 0,
  class: "p-2.5 text-gray-900"
}
const _hoisted_23 = ["onClick"]
const _hoisted_24 = { class: "text-base leading-6 font-semibold text-gray-700" }
const _hoisted_25 = {
  key: 0,
  class: "flex flex-col pt-3"
}
const _hoisted_26 = ["onClick"]

import { computed, onMounted, ref, watch } from "vue"
import GgmsInput from "@/components/GgmsInput.vue"
import { ArrowLeftIcon, SearchIcon } from "@heroicons/vue/outline"
import { Attribute, AttributeType } from "@/shared/models/attributes"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { useConfigStore } from "@/stores/config"
import { getAttributesByCategories, getIconByType } from "@/shared/utils/helpers"
import { PropertyGroup } from "@/shared/models/property-group-layout"
import ArrowDown from "@/components/icons/ArrowDown.vue"


export default /*@__PURE__*/_defineComponent({
  __name: 'AttributeList',
  emits: ["onAttributeSelect", "cancel"],
  setup(__props, { emit: __emit }) {

const gridCollectionStore = useGridCollectionStore()
const configStore = useConfigStore()

const emit = __emit

const searchQuery = ref("")
const prototypes = computed(() => gridCollectionStore.prototypes)
const canGoBack = computed(() => gridCollectionStore.collectionTypeHistory.length > 1)
const propertyLayoutGroups = ref<PropertyGroup[]>([])

// Initialize all groups as open by default
const openGroups = ref<Record<string, boolean>>({})

const sortedNonEmptyCategories = computed(() => {
    return prototypes.value.flatMap((prototype) => {
        let categories = getAttributesByCategories(prototype, [], true) || []
        categories = categories.filter((category) => {
            const visibleAttrs = getFilteredAttributes(category.attributes)
            let hasNonEmptySub = false
            if (category.subGroups && category.subGroups.length) {
                hasNonEmptySub = category.subGroups.some((sub) => {
                    return getFilteredAttributes(sub.properties).length > 0
                })
            }
            return visibleAttrs.length > 0 || hasNonEmptySub
        })
        return categories.sort((a, b) =>
            (a.displayName ?? "").toLowerCase().localeCompare((b.displayName ?? "").toLowerCase())
        )
    })
})

const visiblePropertyGroups = computed(() => {
    return propertyLayoutGroups.value.filter((group) => {
        const groupVisible = hasVisibleProperties(group.properties)
        const subGroupsVisible = group.subGroups?.some((sub) => hasVisibleProperties(sub.properties)) || false
        return groupVisible || subGroupsVisible
    })
})

const getFilteredInboxCategories = (prototype: any) => {
    if (!prototype.categorizedAttributes) return []
    return Object.entries(prototype.categorizedAttributes).filter(
        ([_, attributes]) => getFilteredAttributes(attributes as Attribute[]).length > 0
    )
}

function hasVisibleProperties(properties: Attribute[] = []) {
    return getFilteredAttributes(properties).length > 0
}

function getVisibleSubGroups(group: PropertyGroup) {
    return group.subGroups?.filter((sub) => hasVisibleProperties(sub.properties)) || []
}

function getFilteredAttributes(attributes: Attribute[] = []) {
    if (!Array.isArray(attributes)) {
        return [] // return an empty array if attributes is undefined or not an array
    }

    return attributes
        .filter(
            (attribute) =>
                attribute.displayName.toLowerCase().includes(searchQuery.value.toLowerCase()) &&
                attribute.name !== "_id" &&
                attribute.name !== "password"
        )
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
}

function selectAttribute(attribute: Attribute) {
    emit("onAttributeSelect", attribute)
}

function cancelFilter() {
    emit("cancel")
}

// Toggle the group open/closed
function toggleGroup(groupName: string) {
    openGroups.value[groupName] = !openGroups.value[groupName]
}

function useFill(type: string) {
    return type === AttributeType.Boolean
}

function openAllGroups() {
    propertyLayoutGroups.value = configStore.crmPropertyGroups

    // Open all main groups and their subgroups
    propertyLayoutGroups.value.forEach((group: PropertyGroup) => {
        openGroups.value[group.name] = true

        if (group.subGroups) {
            group.subGroups.forEach((subGroup: { name: string }) => {
                openGroups.value[subGroup.name] = true
            })
        }
    })

    // Open prototypes' categories and subgroups
    prototypes.value.forEach((prototype) => {
        if (prototype.typeName === "inbox" && prototype.categorizedAttributes) {
            Object.keys(prototype.categorizedAttributes).forEach((category) => {
                openGroups.value[category] = true
            })
        }
        const categories = getAttributesByCategories(prototype, [], true)
        categories.forEach((category) => {
            openGroups.value[category.name as string] = true

            if (category.subGroups) {
                category.subGroups.forEach((subGroup: { name: string }) => {
                    openGroups.value[subGroup.name] = true
                })
            }
        })
    })
}

onMounted(() => {
    openAllGroups()
})

// Watch for changes in the page or collection type and re-open groups
watch(
    () => gridCollectionStore.prototypes,
    () => {
        openAllGroups()
    }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (canGoBack.value)
        ? (_openBlock(), _createBlock(_unref(ArrowLeftIcon), {
            key: 0,
            class: "h-6 w-6 text-primary-color-700 cursor-pointer shrink-0",
            onClick: _withModifiers(cancelFilter, ["stop"])
          }))
        : _createCommentVNode("", true),
      _createVNode(GgmsInput, {
        modelValue: searchQuery.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
        iconStart: _unref(SearchIcon),
        placeholder: "Search",
        class: "w-full"
      }, null, 8, ["modelValue", "iconStart"])
    ]),
    _createElementVNode("div", null, [
      (_unref(gridCollectionStore).collectionType === 'listings')
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePropertyGroups.value, (group, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: group.name,
                class: _normalizeClass({ 'mt-4': index > 0 })
              }, [
                _createElementVNode("div", {
                  class: "flex items-center gap-x-2 pt-3 pb-3 px-4 cursor-pointer bg-primary-color-100 border border-gray-300 rounded-lg shadow-sm",
                  onClick: ($event: any) => (toggleGroup(group.name))
                }, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(group.name), 1),
                  (openGroups.value[group.name])
                    ? (_openBlock(), _createBlock(ArrowDown, {
                        key: 0,
                        class: "fill-gray-700"
                      }))
                    : (_openBlock(), _createBlock(ArrowDown, {
                        key: 1,
                        class: "rotate-180 fill-gray-700"
                      }))
                ], 8, _hoisted_4),
                _createVNode(_Transition, { name: "fade" }, {
                  default: _withCtx(() => [
                    (openGroups.value[group.name])
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredAttributes(group.properties), (attribute) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: attribute._id,
                              class: "p-2.5 gap-x-2 text-sm leading-5 font-medium text-gray-500 hover:text-gray-900 cursor-pointer hover:bg-gray-100 rounded-lg flex group",
                              onClick: _withModifiers(($event: any) => (selectAttribute(attribute)), ["stop"])
                            }, [
                              (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(getIconByType)(attribute.type, attribute.name)), {
                                class: _normalizeClass([
                                        useFill(attribute.type)
                                            ? 'fill-gray-500 group-hover:fill-gray-900'
                                            : 'stroke-gray-500 group-hover:stroke-gray-900',
                                        'w-5 h-5 text-gray-500  group-hover:text-gray-900',
                                    ])
                              }, null, 8, ["class"])),
                              _createElementVNode("span", _hoisted_8, _toDisplayString(attribute.displayName), 1)
                            ], 8, _hoisted_7))
                          }), 128)),
                          (group.subGroups)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getVisibleSubGroups(group), (subGroup) => {
                                  return (_openBlock(), _createElementBlock("div", {
                                    key: subGroup.name
                                  }, [
                                    _createElementVNode("div", {
                                      class: "flex gap-x-2 pt-3 cursor-pointer",
                                      onClick: ($event: any) => (toggleGroup(subGroup.name))
                                    }, [
                                      _createElementVNode("span", _hoisted_11, _toDisplayString(subGroup.name), 1),
                                      (openGroups.value[subGroup.name])
                                        ? (_openBlock(), _createBlock(ArrowDown, {
                                            key: 0,
                                            class: "fill-gray-700"
                                          }))
                                        : (_openBlock(), _createBlock(ArrowDown, {
                                            key: 1,
                                            class: "rotate-180 fill-gray-700"
                                          }))
                                    ], 8, _hoisted_10),
                                    _createVNode(_Transition, { name: "fade" }, {
                                      default: _withCtx(() => [
                                        (openGroups.value[subGroup.name])
                                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredAttributes(subGroup.properties), (attribute) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                  key: attribute._id,
                                                  class: "p-2.5 gap-x-2 text-sm leading-5 font-medium text-gray-500 hover:text-gray-900 cursor-pointer hover:bg-gray-100 rounded-lg flex group",
                                                  onClick: _withModifiers(($event: any) => (selectAttribute(attribute)), ["stop"])
                                                }, [
                                                  (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(getIconByType)(attribute.type, attribute.name)), {
                                                    class: _normalizeClass([
                                                        useFill(attribute.type)
                                                            ? 'fill-gray-500 group-hover:fill-gray-900'
                                                            : 'stroke-gray-500 group-hover:stroke-gray-900',
                                                        'w-5 h-5 text-gray-500  group-hover:text-gray-900',
                                                    ])
                                                  }, null, 8, ["class"])),
                                                  _createElementVNode("span", _hoisted_14, _toDisplayString(attribute.displayName), 1)
                                                ], 8, _hoisted_13))
                                              }), 128))
                                            ]))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _: 2
                                    }, 1024)
                                  ]))
                                }), 128))
                              ]))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1024)
              ], 2))
            }), 128))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_15, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prototypes.value, (prototype) => {
              return (_openBlock(), _createElementBlock("div", {
                key: prototype.typeName
              }, [
                (prototype.typeName === 'inbox' && prototype.categorizedAttributes)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredInboxCategories(prototype), ([category, attributes]) => {
                        return (_openBlock(), _createElementBlock("div", { key: category }, [
                          _createElementVNode("div", {
                            class: "flex gap-x-2 pt-3 cursor-pointer",
                            onClick: ($event: any) => (toggleGroup(String(category)))
                          }, [
                            _createElementVNode("span", _hoisted_18, _toDisplayString(category), 1),
                            (openGroups.value[String(category)])
                              ? (_openBlock(), _createBlock(ArrowDown, {
                                  key: 0,
                                  class: "fill-gray-700"
                                }))
                              : (_openBlock(), _createBlock(ArrowDown, {
                                  key: 1,
                                  class: "rotate-180 fill-gray-700"
                                }))
                          ], 8, _hoisted_17),
                          _createVNode(_Transition, { name: "fade" }, {
                            default: _withCtx(() => [
                              (openGroups.value[String(category)])
                                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredAttributes(attributes as Attribute[]), (attribute) => {
                                      return (_openBlock(), _createElementBlock("div", {
                                        key: attribute._id,
                                        class: "p-2.5 gap-x-2 text-sm leading-5 font-medium text-gray-500 hover:text-gray-900 cursor-pointer hover:bg-gray-100 rounded-lg flex group",
                                        onClick: _withModifiers(($event: any) => (selectAttribute(attribute)), ["stop"])
                                      }, [
                                        (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(getIconByType)(attribute.type, attribute.name)), {
                                          class: _normalizeClass([
                                                useFill(attribute.type)
                                                    ? 'fill-gray-500 group-hover:fill-gray-900'
                                                    : 'stroke-gray-500 group-hover:stroke-gray-900',
                                                'w-5 h-5 text-gray-500 group-hover:text-gray-900',
                                            ])
                                        }, null, 8, ["class"])),
                                        _createElementVNode("span", null, _toDisplayString(attribute.displayName), 1)
                                      ], 8, _hoisted_20))
                                    }), 128))
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1024)
                        ]))
                      }), 128))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_21, [
                      (getFilteredAttributes(prototype.attributes).length === 0 && searchQuery.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, " No results "))
                        : _createCommentVNode("", true),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedNonEmptyCategories.value, (category) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: category._id
                        }, [
                          _createElementVNode("div", {
                            class: "flex gap-x-2 pt-3 cursor-pointer",
                            onClick: ($event: any) => (toggleGroup(category.name ?? ''))
                          }, [
                            _createElementVNode("span", _hoisted_24, _toDisplayString(category.displayName), 1),
                            (openGroups.value[category.name ?? ''])
                              ? (_openBlock(), _createBlock(ArrowDown, {
                                  key: 0,
                                  class: "fill-gray-700"
                                }))
                              : (_openBlock(), _createBlock(ArrowDown, {
                                  key: 1,
                                  class: "rotate-180 fill-gray-700"
                                }))
                          ], 8, _hoisted_23),
                          _createVNode(_Transition, { name: "fade" }, {
                            default: _withCtx(() => [
                              (openGroups.value[category.name ?? ''])
                                ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredAttributes(category.attributes), (attribute) => {
                                      return (_openBlock(), _createElementBlock("div", {
                                        key: attribute._id,
                                        class: "p-2.5 gap-x-2 text-sm leading-5 font-medium text-gray-500 hover:text-gray-900 cursor-pointer hover:bg-gray-100 rounded-lg flex group",
                                        onClick: _withModifiers(($event: any) => (selectAttribute(attribute)), ["stop"])
                                      }, [
                                        (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(getIconByType)(attribute.type, attribute.name)), {
                                          class: _normalizeClass([
                                                useFill(attribute.type)
                                                    ? 'fill-gray-500 group-hover:fill-gray-900'
                                                    : 'stroke-gray-500 group-hover:stroke-gray-900',
                                                'w-5 h-5 text-gray-500 group-hover:text-gray-900',
                                            ])
                                        }, null, 8, ["class"])),
                                        _createElementVNode("span", null, _toDisplayString(attribute.displayName), 1)
                                      ], 8, _hoisted_26))
                                    }), 128))
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1024)
                        ]))
                      }), 128))
                    ]))
              ]))
            }), 128))
          ]))
    ])
  ]))
}
}

})